import React, {Component} from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Griddle from 'griddle-react';

import * as PostProps from 'js/common/proptypes/post_props';
import {numberWithCommas, percent, prettyNumber} from 'js/common/string_helper';
import {DatatablePager} from 'js/components/griddle_datatable';
import * as ObjectHelper from 'js/common/object_helper';

import 'scss/griddle_datatable.scss';
import HelpBubble from 'js/components/help_bubble';

const numberWithCommasSpan = ({data}) => <span>{numberWithCommas(data)}</span>;

const imageColumn = {
    columnName: 'image_url',
    displayName: 'Asset',
    customComponent: ({data, rowData}) => {
        if (data === 'Total') {
            return <span>{data}</span>;
        }
        const {setSelectedPost, content_metadata_id} = rowData;
        const style = {
            backgroundImage: `url(${data})`
        };

        return (
            <div
                className="img-post"
                style={style}
                onClick={() => setSelectedPost(content_metadata_id)}
            />
        );
    }
};

const usernameColumn = {
    columnName: 'username',
    displayName: 'User Handle',
    customComponent: props => {
        const {data, rowData} = props;

        const url = rowData.profileUrl || '';

        return <a href={url} target="_blank" rel="noreferrer">{data}</a>;
    }
};

const postColumn = {
    columnName: 'post',
    displayName: 'Post',
    customComponent: props => {
        const {data} = props;

        return <a href={data} target="_blank" rel="noreferrer">{data}</a>;
    }
};

const followersColumn = {
    columnName: 'followers',
    displayName: 'Followers',
    customComponent: numberWithCommasSpan
};

const reachColumn = {
    columnName: 'reach',
    displayName: 'Reach',
    customComponent: numberWithCommasSpan
};


const viewsColumn = {
    columnName: 'views',
    displayName: 'Views',
    customComponent: numberWithCommasSpan
};


const impressionsColumn = {
    columnName: 'impressions',
    displayName: 'Impressions',
    customComponent: numberWithCommasSpan
};

const likesColumn = {
    columnName: 'likes',
    displayName: 'Likes',
    customComponent: numberWithCommasSpan
};

const commentsColumn = {
    columnName: 'comments',
    displayName: 'Comments',
    customComponent: numberWithCommasSpan
};

const sharesColumn = {
    columnName: 'shares',
    displayName: 'Shares',
    customComponent: numberWithCommasSpan
};

const engagementsColumn = {
    columnName: 'engagements',
    displayName: 'Engagements',
    customComponent: numberWithCommasSpan
};

const engagementRateColumn = {
    columnName: 'engagement_rate',
    displayName: 'Eng.rate',
    customComponent: ({data}) => <span>{data ? percent(data) : 'N/A'}</span>
};

const koerColumn = {
    columnName: 'koer',
    displayName: 'Engagement Rate',
    customComponent: ({data}) => <span>{prettyNumber(data, 1)}</span>
};

const sparkAdColumn = (copyToClipboardHandler) => ({
    columnName: 'spark_ad',
    displayName: <><span>{'Spark Ad Auth'}</span><HelpBubble
    text='Request authorization from creator for TikTok Spark Ads. Once approved copy auth code and provide to advertiser.'
    place='top'
    multiline
    inline
    tooltipColor='dark'
/></>,
    customComponent: ({data}) => {
        if (data) {
            const {data: sparkData, handleRequestSparkAuth} = data;

            if (sparkData) {
                const ACCEPTED = sparkData.authStatus === 'ACCEPTED' ||
                    (sparkData.authStatus === 'WAITING' &&
                        sparkData.authData.spark_ads_auth_code);

                const WAITING = (sparkData.authStatus === 'WAITING' &&
                    !sparkData.authData.spark_ads_auth_code);

                if (WAITING) {
                    return (
                        <div className="layout-row layout-row--center">
                            <div className="spark-icon-requested" />
                            <div className="layout-stack spark-text">
                                Requested
                                <a onClick={handleRequestSparkAuth}>
                                    Request Again
                                </a>
                            </div>
                        </div>
                    );
                }

                if (sparkData.authStatus === 'REJECTED') {
                    return (
                        <div className="layout-row layout-row--center">
                            <div className="spark-icon-rejected" />
                            <div className="layout-stack spark-text">
                                Rejected
                                <a onClick={handleRequestSparkAuth}>
                                    Request Again
                                </a>
                            </div>
                        </div>
                    );
                }

                if (ACCEPTED) {
                    const code = sparkData.authData.spark_ads_auth_code;
                    return (
                        <div className="layout-row layout-row--center">
                            <div className="spark-icon-approved" />
                            <div className="spark-text">
                                Approved
                                {code &&
                                    <>
                                        <div
                                            className="spark-code"
                                            onClick={() => copyToClipboardHandler(code)}
                                        >
                                            {`${code.substring(0, 10)}...`}
                                        </div>
                                        <span className="fa fa-solid fa-copy" />
                                    </>
                                }
                            </div>
                        </div>
                    );
                }
            }

            if (handleRequestSparkAuth) {
                return (
                    <button onClick={handleRequestSparkAuth} className="btn btn-info btn-spark">
                        <i className="fa fa-tiktok"/>
                        Request Authorization
                    </button>
                );
            }
        }
        return <></>;
    }
});

export default class PostsList extends Component {
    static propTypes = {
        posts: PropTypes.arrayOf(PropTypes.shape(PostProps.POST)),
        setPostScore: PropTypes.func.isRequired,
        setSelectedPost: PropTypes.func.isRequired,
        showKoer: PropTypes.number.isRequired,
        campaignType: PropTypes.string,
        handleRequestSparkAdAuth: PropTypes.func,
        handleCopyToClipboard: PropTypes.func.isRequired
    };

    handleClickScore = (e, score, postId) => {
        e.preventDefault();
        e.stopPropagation();
        const {setPostScore} = this.props;

        setPostScore(score, postId);
    };

    handleRequestSparkAuth = (e, videoId) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.props.handleRequestSparkAdAuth) {
            this.props.handleRequestSparkAdAuth(videoId);
        }
    };

    calculateTotalRow = (posts) => {
        if (!posts || !posts.length) {
            return undefined;
        }

        const resultRow = {
            image_url: 'Total', // In order to render Total in the first column
            username: '',
            post: '',
            followers: 0,
            reach: 0,
            impressions: 0,
            views: 0,
            likes: 0,
            comments: 0,
            shares: 0,
            engagements: 0,
            engagement_rate: 0
        };

        let engagement_rate_count = 0;

        posts.forEach((post) => {
            if (post.followers) {
                resultRow.followers += parseInt(post.followers, 10);
            }
            if (post.reach) {
                resultRow.reach += parseInt(post.reach, 10);
            }
            if (post.impressions) {
                resultRow.impressions += parseInt(post.impressions, 10);
            }
            if (post.views) {
                resultRow.views += parseInt(post.views, 10);
            }
            if (post.likes) {
                resultRow.likes += parseInt(post.likes, 10);
            }
            if (post.comments) {
                resultRow.comments += parseInt(post.comments, 10);
            }
            if (post.shares) {
                resultRow.shares += parseInt(post.shares, 10);
            }
            if (post.engagements) {
                resultRow.engagements += parseInt(post.engagements, 10);
            }
            if (post.engagement_rate) {
                resultRow.engagement_rate += parseFloat(post.engagement_rate);
                engagement_rate_count = engagement_rate_count + 1;
            }

        });


        if (resultRow.engagement_rate) {
            // Show an average for the engagement rate ( not a sum )
            resultRow.engagement_rate = resultRow.engagement_rate / engagement_rate_count;
        }

        return resultRow;
    }

    getUsernameAndProfileUrl = (post) => {
        const {
            content_distribution_metadata: contentDistributionMetadata,
            username,
            tiktok_username
        } = post;
        const {campaignType} = this.props;

        let url = '';
        let usernameToDisplay = username;
        let isTikTok = campaignType === 'tiktok';

        // If content is distributed, check distribution metadata to check distribution platform
        if (contentDistributionMetadata) {
            const postUrl = R.path(['distribution_platform_post_url'], contentDistributionMetadata)
                || '';

            isTikTok = postUrl.includes('tiktok.com');

            usernameToDisplay = ObjectHelper.pathOr(
                username,
                ['snapshots', 'platform_account', 'platform_handle'],
                contentDistributionMetadata
            );
        }

        // If post is distributed to TikTok
        // or content is not yet distributed and a campaign is TikTok
        // then display TikTok username as well as TikTok profile url
        if (isTikTok && tiktok_username) {
            usernameToDisplay = tiktok_username;
            url = `//tiktok.com/@${tiktok_username}`;
        } else {
            url = `//instagram.com/${usernameToDisplay}`
        }

        return {username: usernameToDisplay, profileUrl: url};
    };

    getGriddleColumns = () => {
        const {posts, setSelectedPost, campaignType} = this.props;

        const convertedPosts = posts.map(post => {
            const {
                media_id,
                content_distribution_metadata,
                content_metadata_id,
                post_id,
                thumbnail_url,
                koer,
                engagements,
                engagement_rate,
                spark_ad
            } = post;

            const {username, profileUrl} = this.getUsernameAndProfileUrl(post);

            const spark_ad_data = spark_ad ? {
                data: {
                    authStatus: spark_ad.authStatus,
                    authData: spark_ad.authData,
                    videoId: spark_ad.videoId
                },
                handleRequestSparkAuth: (e) => this.handleRequestSparkAuth(e, spark_ad.videoId)
            }: {};

            const impressions = R.path(['snapshots', 'social_engagements', 'impressions'])(
                content_distribution_metadata
            );

            const views = R.path(['snapshots', 'social_engagements', 'views'])(
                content_distribution_metadata
            );

            const postUrl = R.prop('distribution_platform_post_url', content_distribution_metadata);

            const isTikTokPost = postUrl && postUrl.includes('tiktok.com');

            return {
                image_url: thumbnail_url,
                username,
                post: postUrl,
                likes: R.path(['snapshots', 'social_engagements', 'likes'])(
                    content_distribution_metadata
                ),
                comments: R.path(['snapshots', 'social_engagements', 'replies'])(
                    content_distribution_metadata
                ),
                shares: R.path(['snapshots', 'social_engagements', 'shares'])(
                    content_distribution_metadata
                ),
                reach: R.path(['snapshots', 'social_engagements', 'reach'])(
                    content_distribution_metadata
                ),
                views: isTikTokPost ? views : impressions,
                impressions: isTikTokPost ? views : impressions,
                followers: R.path(['snapshots', 'platform_account', 'follower_count'])(
                    content_distribution_metadata
                ),
                engagements,
                engagement_rate: engagement_rate || undefined,
                koer,
                media_id,
                content_metadata_id,
                handleClickScore: (e, score) => this.handleClickScore(e, score, post_id),
                handleRequestSparkAuth: (e, media_id) => this.handleRequestSparkAuth(e, media_id),
                setSelectedPost,
                campaignType,
                spark_ad: spark_ad_data,
                profileUrl
            };
        });

        const resultRow = this.calculateTotalRow(convertedPosts);

        if (resultRow) {
            return [...convertedPosts, resultRow];
        }

        return convertedPosts;
    };

    render() {
        const {showKoer, campaignType} = this.props;

        const resultsInfo = this.getGriddleColumns();
        const engRateColumn = showKoer ? koerColumn : engagementRateColumn;
        const griddleColumns = [
            imageColumn,
            usernameColumn,
            postColumn,
            followersColumn,
            reachColumn,
            campaignType === 'tiktok' ? viewsColumn : impressionsColumn,
            likesColumn,
            commentsColumn,
            sharesColumn,
            engagementsColumn,
            engRateColumn
        ];

        let headerValue;

        if (campaignType === 'tiktok') {
            headerValue = 'TikTok';
            griddleColumns.push(sparkAdColumn(this.props.handleCopyToClipboard));
        } else if (campaignType === 'instagram') {
            headerValue = 'Instagram';
        }

        const nextPage = (
            <span className="pagination-button">
                <i className="fa fa-arrow-right"></i>
            </span>
        );

        const prevPage = (
            <span className="pagination-button">
                <i className="fa fa-arrow-left"></i>
            </span>
        );

        return (
            <div>
                {headerValue && (
                    <h2 className="list-mode-header">{headerValue} instagram</h2>
                )}
                <Griddle
                    results={resultsInfo}
                    resultsPerPage={4000}
                    tableClassName="table"
                    showFilter={false}
                    showSettings={false}
                    columns={R.pluck('columnName', griddleColumns)}
                    columnMetadata={griddleColumns}
                    useCustomPagerComponent
                    customPagerComponent={DatatablePager}
                    sortAscendingComponent={<span className="fa fa-caret-up pull-left" />}
                    sortDescendingComponent={<span className="fa fa-caret-down pull-left" />}
                    useGriddleStyles={false}
                    nextText={nextPage}
                    previousText={prevPage}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./campaign_analytics/v2/campaign_analytics_page/posts/posts_list/index.js